/**
 * Message contract between the editor and the spelling worker. The worker owns the Australian English
 * Hunspell dictionary; the editor only sends words and receives verdicts or suggestions.
 */

export const MAX_WORDS_PER_CHECK = 2000;
export const MAX_SUGGESTIONS = 5;
export const MAX_WORD_LENGTH = 64;

export type SpellWorkerRequest =
  | { type: 'check'; id: number; words: readonly string[] }
  | { type: 'suggest'; id: number; word: string };

export type SpellWorkerResponse =
  | { type: 'checked'; id: number; misspelled: string[] }
  | { type: 'suggestions'; id: number; word: string; suggestions: string[] }
  | { type: 'error'; id: number; message: string };

/** Minimal shape of a Hunspell checker, so the word rules can be tested without the browser worker. */
export interface HunspellLike {
  correct(word: string): boolean;
  suggest(word: string): string[];
}

/** Curly apostrophes are checked as straight ones; the dictionary only contains the latter. */
export function normaliseWord(word: string): string {
  return word.replace(/[\u2018\u2019\u02bc]/g, "'");
}

function possessiveBase(word: string): string | null {
  return /'s$/i.test(word) && word.length > 2 ? word.slice(0, -2) : null;
}

/** Accepts a word when the dictionary knows it, or knows its base for possessives such as organisation's. */
export function isWordCorrect(spell: HunspellLike, rawWord: string): boolean {
  const word = normaliseWord(rawWord);
  if (word.length > MAX_WORD_LENGTH) return true;
  const base = possessiveBase(word);
  return spell.correct(word) || (base !== null && spell.correct(base));
}

/** Up to five replacements; possessive words keep their 's. */
export function suggestionsFor(spell: HunspellLike, rawWord: string): string[] {
  const word = normaliseWord(rawWord);
  if (word.length > MAX_WORD_LENGTH) return [];
  const base = possessiveBase(word);
  const results = base !== null && !spell.correct(base)
    ? spell.suggest(base).map(candidate => candidate + "'s")
    : spell.suggest(word);
  const unique: string[] = [];
  for (const candidate of results) {
    if (candidate !== word && !unique.includes(candidate)) unique.push(candidate);
    if (unique.length >= MAX_SUGGESTIONS) break;
  }
  return unique;
}
